import { StageController } from './StageController'
import { Texture } from 'three'
import { loadImage } from '../lib/loadImage'
import { Pane } from 'tweakpane'

const PARAMS = {
  count: 47,
  radius: 1,
  twist: 1,
  loops: 1,
  fog: 0,
  thickness: 0.35,
  edge: 0.25,
  edgeAlpha: 0.2,
  grain: 0,
}

const CONFIG = {
  speed: 1,
  fade: 1,
  spin: 1,
  tilt: 0,
  pivot: 0,
  scale: 1,
  position: { x: 0, y: 0 },
  rotation: { x: 0, y: 0 },
}

export class Edit {
  private active = false

  constructor(private readonly stage: StageController) {
    PARAMS.grain = 0.035 * this.stage.renderer.getPixelRatio()

    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.multiple = true
    input.style.position = 'fixed'
    input.style.bottom = '0'
    input.style.left = '0'
    input.addEventListener('change', this.buildTexture.bind(this))

    document.body.appendChild(input)

    const container = document.createElement('div')
    container.style.position = 'fixed'
    container.style.top = '0'
    container.style.left = '0'

    document.body.appendChild(container)

    const pane = new Pane({ container })
    const b = pane.addFolder({ title: 'base' })
    b.addBinding(PARAMS, 'count', { min: 1, max: 200, step: 1 })
    b.addBinding(PARAMS, 'radius', { min: 0.5, max: 3 })
    b.addBinding(PARAMS, 'twist', { min: 0, max: 1 })
    b.addBinding(PARAMS, 'loops', { min: 0, max: 10, step: 1 })
    b.addBinding(PARAMS, 'fog', { min: 0, max: 1 })
    b.addBinding(PARAMS, 'thickness', { min: 0.1, max: 2, step: 0.001 })
    b.addBinding(PARAMS, 'edge', { min: 0, max: 0.5 })
    b.addBinding(PARAMS, 'edgeAlpha', { min: 0, max: 1 })
    b.addBinding(PARAMS, 'grain', { min: 0, max: 0.5, step: 0.001 })

    const c = pane.addFolder({ title: 'config' })
    c.addBinding(CONFIG, 'speed', { min: 0, max: 3 })
    c.addBinding(CONFIG, 'fade', { min: 0, max: 10 })
    c.addBinding(CONFIG, 'spin', { min: 0, max: 3 })
    c.addBinding(CONFIG, 'tilt', { min: -0.5, max: 0.5, step: 0.001 })
    c.addBinding(CONFIG, 'pivot', { min: 0, max: 0.5, step: 0.001 })
    c.addBinding(CONFIG, 'scale', { min: 0.1, max: 5 })
    c.addBinding(CONFIG, 'position', {
      picker: 'inline',
      expanded: true,
      x: { min: -1, max: 1 },
      y: { min: -1, max: 1 },
    })
    c.addBinding(CONFIG, 'rotation', {
      picker: 'inline',
      expanded: true,
      x: { min: Math.PI * -0.5, max: Math.PI * 0.5 },
      y: { min: Math.PI * -0.5, max: Math.PI * 0.5 },
    })

    c.addButton({ title: 'export config' }).on('click', async () => {
      await navigator.clipboard.writeText(JSON.stringify(CONFIG))
      console.log('copied to clipboard')
      console.log(JSON.stringify(CONFIG))
    })

    pane.addButton({ title: 'download image' }).on('click', () => {
      const link = document.createElement('a')
      link.download = 'render.png'
      link.href = this.stage.renderer.domElement.toDataURL('image/png')
      link.click()
      link.parentNode?.removeChild(link)
    })

    input.style.display = 'none'
    container.style.display = 'none'

    this.active = false

    document.addEventListener('keypress', (event) => {
      if (event.key === 'h') {
        this.active = !this.active
        input.style.display = this.active ? 'block' : 'none'
        container.style.display = this.active ? 'block' : 'none'
      }
    })
  }

  async buildTexture(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0]
    if (!file) return
    const image = await loadImage(URL.createObjectURL(file))
    const texture = new Texture(image)
    texture.needsUpdate = true
    this.stage.moebius.setTexture(texture)
  }

  update() {
    if (!this.active) return

    const { moebius } = this.stage
    moebius.uniforms.uCount.value = PARAMS.count
    moebius.uniforms.uThickness.value = PARAMS.thickness
    moebius.uniforms.uRadius.value = PARAMS.radius
    moebius.uniforms.uTwist.value = PARAMS.twist
    moebius.uniforms.uLoops.value = PARAMS.loops
    moebius.uniforms.uGrain.value = PARAMS.grain * this.stage.renderer.getPixelRatio()
    moebius.uniforms.uEdge.value = PARAMS.edge
    moebius.uniforms.uFog.value = PARAMS.fog
    moebius.uniforms.uEdgeAlpha.value = PARAMS.edgeAlpha
    moebius.mesh.count = PARAMS.count

    moebius.config.speed.setValue(CONFIG.speed)
    moebius.config.fade.setValue(CONFIG.fade)
    moebius.config.spin.setValue(CONFIG.spin)

    moebius.uniforms.uPivot.value = CONFIG.pivot
    moebius.uniforms.uTilt.value = CONFIG.tilt
    moebius.uniforms.uScale.value = CONFIG.scale
    moebius.uniforms.uRotation.value.copy(CONFIG.rotation)
    moebius.uniforms.uPosition.value.copy(CONFIG.position)
  }
}
